import { useAuth } from '@sonnen/web-utils/hooks';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../AppContext/AppContext';
import { DEFAULT_OFFER_CONTEXT, DEFAULT_PERSONS, initialOfferInput } from '../constants';
import { CreateProductConfigurationsInput, ProductConfigurations } from '../graphql/generated';
import { Battery, trackEvent } from '../gtm';
import {
  CurrentUser,
  ModuleName,
  OfferContextType,
  OfferErrorTranslationKeyType,
  OrderFlow,
} from '../types';
import { hasSonnenStromContract, moduleAvailable } from '../utils/utils';

type OfferContextProviderProps = {
  children: React.ReactNode;
  initialValues?: Pick<
    OfferContextType,
    | 'offer'
    | 'productConfigurationsInput'
    | 'personsInHousehold'
    | 'quarterlyPriceData'
    | 'offerLoading'
  >;
};

const inputKey = import.meta.env.VITE_HONEYPOT_KEY;

export const OfferContext = createContext<OfferContextType>(DEFAULT_OFFER_CONTEXT);

export const useOfferContext = () => useContext(OfferContext);

export const OfferContextProvider = ({ children, initialValues }: OfferContextProviderProps) => {
  const { authenticated } = useAuth<CurrentUser>();
  const [workEmail, setWorkEmail] = useState<string>('');
  const [offer, setOffer] = useState<ProductConfigurations | null>(initialValues?.offer || null);
  const [offerError, setOfferError] = useState<OfferErrorTranslationKeyType>(null);
  const [offerLoading, setOfferLoading] = useState(initialValues?.offerLoading || false);
  const [personsInHousehold, setPersonsInHousehold] = useState<number>(DEFAULT_PERSONS);
  const [productConfigurationsInput, setProductConfigurationsInput] =
    useState<CreateProductConfigurationsInput>(
      initialValues?.productConfigurationsInput || initialOfferInput,
    );

  const { orderFlow, modules, selectedSite } = useAppContext();

  useEffect(() => {
    if (navigator.userAgent.includes('Edg')) setWorkEmail(inputKey);
  }, []);

  useEffect(() => {
    if (navigator.userAgent.includes('Edg')) return;
    if (authenticated) setWorkEmail(inputKey);
  }, [authenticated]);

  useEffect(() => {
    setOffer(null);
  }, [productConfigurationsInput]);

  useEffect(() => {
    setOfferError(null);
  }, [productConfigurationsInput.deliveryAddress.postalCode]);

  useEffect(() => {
    if (!offer) return;

    const energyDynamic = orderFlow === OrderFlow.energyDynamic;
    const energyFix = orderFlow === OrderFlow.energyFix;

    const available = moduleAvailable(offer, ModuleName.PRICE_OPTIMIZATION);
    const hasBattery = selectedSite?.options?.hasBatteries;

    let battery: Battery = 'missing';

    if (hasBattery) {
      if (!available && !hasSonnenStromContract(selectedSite)) battery = 'incompatible';
      else battery = 'available';
    }

    trackEvent({
      energyFix,
      energyDynamic,
      priceOptimization: energyDynamic && modules.has(ModuleName.PRICE_OPTIMIZATION),
      battery,
    });
  }, [offer, orderFlow, modules, selectedSite]);

  const configuration =
    offer?.energyDynamic?.configurationWithBatteryOptimizationModule ||
    offer?.energyDynamic?.configurationWithoutBatteryOptimizationModule;

  const { hourlyPurchaseTariffGrossPerKwhSeries } = configuration || {};

  const value = useMemo(() => {
    return {
      quarterlyPriceData: hourlyPurchaseTariffGrossPerKwhSeries,
      offer,
      offerError,
      offerLoading,
      personsInHousehold,
      productConfigurationsInput,
      setOffer,
      setOfferError,
      setOfferLoading,
      setPersonsInHousehold,
      setProductConfigurationsInput,
      setWorkEmail,
      workEmail,
    };
  }, [
    offer,
    offerError,
    offerLoading,
    personsInHousehold,
    productConfigurationsInput,
    setOffer,
    setOfferError,
    setOfferLoading,
    setPersonsInHousehold,
    setProductConfigurationsInput,
    setWorkEmail,
    workEmail,
  ]);

  return <OfferContext.Provider value={value}>{children}</OfferContext.Provider>;
};
